import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  Copy,
  Download,
  ExternalLink,
  Eye,
  History,
  Lock,
  MoreHorizontal,
  Redo2,
  Save,
  Share2,
  Undo2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { FOCUS_RING, GHOST_BTN, PILL_INK, PILL_OUTLINE } from './constants';
import { NameField } from './StudioDialogs';

const MENU = 'rounded-2xl border-[#E6E1D6] bg-white p-1.5 min-w-[230px] shadow-[0_18px_40px_-18px_rgba(26,26,26,0.35)]';
const MENU_ITEM = 'rounded-xl px-3 py-2.5 text-sm font-semibold text-ink gap-2.5 cursor-pointer focus:bg-cream';

const SAVE_TEXT = {
  saved: 'All changes saved',
  unsaved: 'Unsaved changes',
  new: 'Not saved yet',
  saving: 'Saving…',
};
const SAVE_DOT = {
  saved: 'bg-sage',
  unsaved: 'bg-[#C9962B]',
  new: 'bg-[#B8B2A5]',
  saving: 'bg-blue animate-pulse',
};

export const ModeToggle = ({ mode, onChange, className }) => (
  <div role="group" aria-label="Editor mode" className={cn('flex p-[3px] rounded-full bg-cream', className)}>
    {[
      ['simple', 'Simple'],
      ['advanced', 'Advanced'],
    ].map(([id, label]) => (
      <button
        key={id}
        type="button"
        aria-pressed={mode === id}
        onClick={() => onChange(id)}
        className={cn(
          'h-[34px] px-3.5 rounded-full text-[13px] font-extrabold',
          mode === id ? 'bg-ink text-paper' : 'text-[#4A4640] hover:text-ink',
          FOCUS_RING
        )}
        data-testid={`mode-${id}`}
      >
        {label}
      </button>
    ))}
  </div>
);

/** Download menu: standard PDF/PNG/JPG and 4K print quality (paid only). */
export const DownloadMenu = ({ locked, disabled, exporting, onDownload, compact, price }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        disabled={disabled || exporting}
        className={cn(PILL_INK, compact ? 'h-10 px-3.5 text-sm' : 'h-10 pl-4 pr-5 text-sm')}
        aria-label={locked ? 'Download (purchase required)' : 'Download'}
        data-testid="download-menu-btn"
      >
        {locked ? <Lock className="h-4 w-4" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}
        {compact ? (
          <span>Download</span>
        ) : (
          <span className="flex flex-col leading-[1.1] text-left">
            <span>{exporting ? 'Preparing…' : 'Download'}</span>
            <span className="text-[10px] font-semibold text-[#CFCAC0]">PDF · PNG · 4K print</span>
          </span>
        )}
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className={MENU}>
      {locked && (
        <>
          <DropdownMenuItem className={cn(MENU_ITEM, 'bg-cream')} onSelect={() => onDownload(null)} data-testid="unlock-downloads">
            <Lock className="h-4 w-4 text-[#6E5B1C]" aria-hidden="true" />
            <span className="flex-1">Unlock downloads</span>
            {price ? <span className="font-serif font-semibold">{price}</span> : null}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}
      <DropdownMenuLabel className="px-3 pt-1.5 pb-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#6B665C]">
        Standard
      </DropdownMenuLabel>
      {['pdf', 'png', 'jpg'].map((format) => (
        <DropdownMenuItem key={format} className={MENU_ITEM} onSelect={() => onDownload(format, 'standard')} data-testid={`download-${format}-btn`}>
          {locked ? <Lock className="h-4 w-4 text-[#9C978C]" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}
          {format.toUpperCase()}
        </DropdownMenuItem>
      ))}
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="px-3 pt-1.5 pb-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#6B665C]">
        Print quality (4K)
      </DropdownMenuLabel>
      {['pdf', 'png'].map((format) => (
        <DropdownMenuItem key={format} className={MENU_ITEM} onSelect={() => onDownload(format, 'print')} data-testid={`download-print-${format}-btn`}>
          {locked ? <Lock className="h-4 w-4 text-[#9C978C]" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}
          {format.toUpperCase()} · print quality
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

/** Share menu. */
export const ShareMenu = ({ invitation, onCopy, compact, trigger }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      {trigger || (
        <button type="button" className={cn(PILL_OUTLINE, 'gb-paper h-10 px-4 text-sm')} data-testid="share-btn">
          <Share2 className="h-4 w-4" aria-hidden="true" />
          {!compact && 'Share'}
        </button>
      )}
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className={MENU}>
      <DropdownMenuItem className={MENU_ITEM} onSelect={onCopy} data-testid="copy-share-link">
        <Copy className="h-4 w-4" aria-hidden="true" /> Copy share link
      </DropdownMenuItem>
      {invitation?.share_link ? (
        <DropdownMenuItem asChild className={MENU_ITEM}>
          <Link to={`/share/${invitation.share_link}`} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-4 w-4" aria-hidden="true" /> Open share page
          </Link>
        </DropdownMenuItem>
      ) : (
        <p className="px-3 py-2 text-xs text-[#6B665C]">Save your invitation to get a share link.</p>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);

/** The studio's top bar (desktop and phone variants). */
export const StudioTopBar = ({
  isPhone,
  onBack,
  name,
  onNameChange,
  saveState,
  subtitle,
  mode,
  onModeChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  canVersions,
  onVersions,
  onPreview,
  invitation,
  onCopyShare,
  onSave,
  saving,
  download,
}) => {
  const undoRedo = (
    <>
      <button type="button" onClick={onUndo} disabled={!canUndo} aria-label="Undo" title="Undo (Ctrl+Z)" className={cn(GHOST_BTN, isPhone ? 'h-11 w-11' : 'h-10 w-10')} data-testid="undo-btn">
        <Undo2 className="h-[19px] w-[19px]" aria-hidden="true" />
      </button>
      <button type="button" onClick={onRedo} disabled={!canRedo} aria-label="Redo" title="Redo (Ctrl+Shift+Z)" className={cn(GHOST_BTN, isPhone ? 'h-11 w-11' : 'h-10 w-10')} data-testid="redo-btn">
        <Redo2 className="h-[19px] w-[19px]" aria-hidden="true" />
      </button>
    </>
  );

  if (isPhone) {
    return (
      <header className="h-14 shrink-0 px-1.5 flex items-center justify-between gap-1 bg-paper gradient-hairline-b">
        <div className="flex items-center min-w-0">
          <button type="button" onClick={onBack} aria-label="Back" className={cn(GHOST_BTN, 'h-11 w-11 shrink-0')} data-testid="back-btn">
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          {undoRedo}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => onModeChange(mode === 'simple' ? 'advanced' : 'simple')}
            className={cn('h-[30px] px-2.5 rounded-full bg-cream text-[11px] font-extrabold', FOCUS_RING)}
            aria-label={`Mode: ${mode === 'simple' ? 'Simple' : 'Advanced'}. Switch to ${mode === 'simple' ? 'Advanced' : 'Simple'}`}
            data-testid="mode-toggle-phone"
          >
            {mode === 'simple' ? 'Simple' : 'Advanced'}
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            aria-label={saving ? 'Saving' : 'Save'}
            className={cn(GHOST_BTN, 'h-11 w-11 relative')}
            data-testid="save-btn"
          >
            <Save className="h-5 w-5" aria-hidden="true" />
            {saveState === 'unsaved' || saveState === 'new' ? (
              <span className="absolute top-2.5 right-2.5 h-2 w-2 rounded-full bg-[#C9962B]" aria-hidden="true" />
            ) : null}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" aria-label="More" className={cn(GHOST_BTN, 'h-11 w-9')} data-testid="more-btn">
                <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={MENU}>
              <DropdownMenuLabel className="px-3 py-1.5 text-xs text-[#6B665C] font-semibold">{SAVE_TEXT[saveState]}</DropdownMenuLabel>
              <DropdownMenuItem className={MENU_ITEM} onSelect={onPreview}>
                <Eye className="h-4 w-4" aria-hidden="true" /> Free preview
              </DropdownMenuItem>
              <DropdownMenuItem className={MENU_ITEM} onSelect={onVersions} disabled={!canVersions}>
                <History className="h-4 w-4" aria-hidden="true" /> Versions
              </DropdownMenuItem>
              <DropdownMenuItem className={MENU_ITEM} onSelect={onCopyShare}>
                <Copy className="h-4 w-4" aria-hidden="true" /> Copy share link
              </DropdownMenuItem>
              {invitation?.share_link && (
                <DropdownMenuItem asChild className={MENU_ITEM}>
                  <Link to={`/share/${invitation.share_link}`} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" aria-hidden="true" /> Open share page
                  </Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <DownloadMenu {...download} compact />
        </div>
      </header>
    );
  }

  return (
    <header className="h-16 shrink-0 pl-3 pr-4 flex items-center justify-between gap-3 bg-paper gradient-hairline-b">
      <div className="flex items-center gap-2.5 min-w-0">
        <button type="button" onClick={onBack} className={cn(GHOST_BTN, 'h-10 pl-2.5 pr-3.5 text-sm shrink-0')} data-testid="back-btn">
          <ChevronLeft className="h-[18px] w-[18px]" aria-hidden="true" />
          <span className="hidden xl:inline">My invitations</span>
          <span className="xl:hidden">Back</span>
        </button>
        <span className="w-px h-6 bg-[#DCD6C8] shrink-0" aria-hidden="true" />
        <div className="flex flex-col min-w-0 pl-1.5">
          <NameField value={name} onChange={onNameChange} className="w-[220px] max-w-full" />
          <p className="text-xs text-[#6B665C] flex items-center gap-1.5 whitespace-nowrap truncate" aria-live="polite">
            <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', SAVE_DOT[saveState])} aria-hidden="true" />
            {SAVE_TEXT[saveState]}
            {subtitle ? <span className="hidden 2xl:inline"> · {subtitle}</span> : null}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        <ModeToggle mode={mode} onChange={onModeChange} className="mr-1.5" />
        {undoRedo}
        <button
          type="button"
          onClick={onVersions}
          disabled={!canVersions}
          title={canVersions ? 'Version history' : 'Save your invitation to keep versions'}
          className={cn(GHOST_BTN, 'h-10 px-3 text-[13px]')}
          data-testid="versions-btn"
        >
          <History className="h-[17px] w-[17px]" aria-hidden="true" />
          <span className="hidden xl:inline">Versions</span>
        </button>
        <span className="w-px h-6 bg-[#DCD6C8] mx-1.5" aria-hidden="true" />
        <button type="button" onClick={onPreview} className={cn(PILL_OUTLINE, 'gb-paper h-10 px-4 text-sm')} data-testid="preview-btn">
          <Eye className="h-4 w-4" aria-hidden="true" />
          <span className="hidden xl:inline">Free preview</span>
          <span className="xl:hidden">Preview</span>
        </button>
        <ShareMenu invitation={invitation} onCopy={onCopyShare} />
        <button type="button" onClick={onSave} disabled={saving} className={cn(PILL_OUTLINE, 'gb-paper h-10 px-4 text-sm')} data-testid="save-btn">
          <Save className="h-4 w-4" aria-hidden="true" />
          {saving ? 'Saving…' : 'Save'}
        </button>
        <DownloadMenu {...download} />
      </div>
    </header>
  );
};

export default StudioTopBar;
